import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useStores } from "stores/setup/use-store";
import {
  DEFAULT_MIN_ADVANCE_TIME,
  LEGACY_MAX_ADVANCE_TIME_FALLBACK,
} from "../constants/SendConfigConstants";
import { AlertModule } from "stores/data/insightAlerts/insightAlertModel";
import { DBAction } from "../../../../enum/DBAction";
import { EditAlertFormValues, AlertChannel } from "pages/Alert/types";
import { useAlertEditFormData } from "./useAlertEditFormData";
import { useAlertEditTemplates } from "./useAlertEditTemplates";
import { useAlertEditUsers } from "./useAlertEditUsers";
import { useAlertEditSubmission } from "./useAlertEditSubmission";
import { extractCoordinatesFromGeoJSON } from "pages/ForecastChart/ForecastChartPanelMap/util/extractCoordinatesFromGeoJSON";
import { getMacroRegionOriginFromPolygon } from "util/macroRegion/getMacroRegionOriginFromPolygon";
import { MacroRegion } from "enum/MacroRegion";
import { useHistory } from "react-router-dom";
import { LatLngLiteral } from "leaflet";
import { StoreState } from "enum/StoreState";
import { LocationType } from "pages/OceanWeatherIntelligence/OperationRuleLocation/Components/LocationTypeSelector";
import { IStation } from "stores/data/oceanWeatherIntelligences/oceanWeatherIntelligenceModel";
import { SensorAlertConfig } from "types/ISensorAlertConfig";

/**
 * Normalizes a geofence to a flat array of LatLngLiteral.
 * Handles both nested (LatLngLiteral[][]) and flat (LatLngLiteral[]) formats.
 */
function normalizeGeofence(
  selectedGeofence: LatLngLiteral[] | undefined,
  apiGeofence: LatLngLiteral[] | LatLngLiteral[][] | undefined
): LatLngLiteral[] {
  // Priority 1: Use selectedGeofence if available (user drew or selected new area)
  if (selectedGeofence && selectedGeofence.length > 0) {
    return selectedGeofence;
  }

  // Priority 2: Normalize and use API geofence
  if (apiGeofence && apiGeofence.length > 0) {
    // Check if it's a nested array (LatLngLiteral[][])
    if (Array.isArray(apiGeofence[0])) {
      return (apiGeofence as LatLngLiteral[][]).flat();
    }
    return apiGeofence as LatLngLiteral[];
  }

  return [];
}

export function useAlertEditDrawer(isOpen: boolean, onClose: () => void) {
  const {
    dataStores: { insightAlertStore, unitMeasurementStore, authStore, regionStore, forecastChartPageStore, locationStore, oceanWeatherIntelligenceStore, operationRuleStore },
  } = useStores();

  const { t } = useTranslation("translation", {
    keyPrefix: "oceanWeatherIntelligence.insightAlert",
  });

  const history = useHistory();

  const formData = useAlertEditFormData();
  const templates = useAlertEditTemplates();
  const users = useAlertEditUsers();
  const submission = useAlertEditSubmission();

  const hasDetectedLocationTypeRef = useRef(false);

  // State to track if initial location type detection is complete
  // This is used to show loading skeleton only during initial detection, not when user navigates back
  const [hasCompletedInitialDetection, setHasCompletedInitialDetection] = useState(false);

  // State for location data warning modal
  const [isLocationWarningModalOpen, setIsLocationWarningModalOpen] = useState(false);
  const pendingBackActionRef = useRef<(() => void) | null>(null);

  // Insight Alert detection and data
  const isInsightAlert = useMemo(() => {
    return !!insightAlertStore.operationRuleAlertForTableForEdit?.operationRuleLocationItemId;
  }, [insightAlertStore.operationRuleAlertForTableForEdit]);

  const insightAlertData = useMemo(() => {
    if (!isInsightAlert) return null;

    const tableData = insightAlertStore.operationRuleAlertForTableForEdit;
    const alertData = insightAlertStore.operationRuleAlert;

    // Get location name from station.name (point) or operationRuleLocationName (area)
    const locationName = alertData?.station?.name || tableData?.operationRuleLocationName || "";

    // Use selectedGeofence if available (user may have drawn a new area), otherwise use alertData.geofence
    const currentGeofence = normalizeGeofence(
      insightAlertStore.selectedGeofence,
      alertData?.geofence
    );

    return {
      dashboardId: tableData?.operationRuleId,
      dashboardName: tableData?.operationRuleName || "",
      insightId: tableData?.operationRuleLocationItemId,
      insightName: tableData?.operationRuleLocationItemName || "",
      locationName,
      alertType: tableData?.alertType || alertData?.alertType,
      // For insight alerts, use 'conditions' returned by backend (not conditionGroups)
      conditions: alertData?.conditions || [],
      station: alertData?.station,
      geofence: currentGeofence,
    };
  }, [isInsightAlert, insightAlertStore.operationRuleAlertForTableForEdit, insightAlertStore.operationRuleAlert, insightAlertStore.selectedGeofence]);

  // Reset ref when drawer closes
  useEffect(() => {
    if (!isOpen) {
      hasDetectedLocationTypeRef.current = false;
      setHasCompletedInitialDetection(false);
      // Reset insight alert selection state when drawer closes
      insightAlertStore.setSelectedInsight(null);
      insightAlertStore.setSelectedInsightLocationId(null);
      insightAlertStore.setSelectedDashboardIdForEdit(null);
    }
  }, [isOpen, insightAlertStore]);

  // Load dashboards and location menu list when editing an insight alert
  useEffect(() => {
    if (isOpen && isInsightAlert && insightAlertData?.dashboardId) {
      // Load dashboards if not already loaded
      if (operationRuleStore.operationRules.length === 0) {
        operationRuleStore.getOperationRulesApi();
      }
      insightAlertStore.getLocationMenuListAPI(insightAlertData.dashboardId);
      insightAlertStore.setSelectedDashboardIdForEdit(insightAlertData.dashboardId);
    }
  }, [isOpen, isInsightAlert, insightAlertData?.dashboardId, insightAlertStore, operationRuleStore]);

  useEffect(() => {
    locationStore.getAllLocationsAPI();
  }, [locationStore]);

  useEffect(() => {
    if (isOpen) {
      unitMeasurementStore.getUserUnitMeasurementApi();
    }
  }, [isOpen, unitMeasurementStore]);

  // Load alert templates when opening the edit drawer
  useEffect(() => {
    if (isOpen && insightAlertStore.alertTemplates.length === 0) {
      insightAlertStore.getAlertTemplateConfig();
    }
  }, [isOpen, insightAlertStore]);

  const macroRegionOrigin = insightAlertStore.operationRuleAlert?.macroRegionOrigin;
  const alertStationId = insightAlertStore.operationRuleAlert?.stationId;
  const alertGeofence = insightAlertStore.operationRuleAlert?.geofence;

  useEffect(() => {
    if (macroRegionOrigin && isOpen) {
      // Build params based on alert data:
      // - Existing station (stationId > 0): add stationId
      // - Custom point (geofence with single point): add latitude/longitude
      // - All other cases: only macroRegionOrigin
      const params: {
        macroRegionOrigin: string;
        stationId?: number;
        latitude?: number;
        longitude?: number;
        forceReload?: boolean;
      } = { macroRegionOrigin, forceReload: true };

      if (alertStationId && alertStationId > 0) {
        // Existing station
        params.stationId = alertStationId;
      } else if (alertGeofence && Array.isArray(alertGeofence) && alertGeofence.length === 1) {
        // Custom point (single point geofence)
        const point = alertGeofence[0] as { lat: number; lng: number };
        if (point && typeof point.lat === 'number' && typeof point.lng === 'number') {
          params.latitude = point.lat;
          params.longitude = point.lng;
        }
      }

      insightAlertStore.getEnvironmentalVariables(params);
    }
  }, [isOpen, macroRegionOrigin, alertStationId, alertGeofence, insightAlertStore]);

  // Load sensor data and populate sensor alert state when editing an alert with sensorAlertConfig (FR-060).
  // Track which alert ID was initialized to avoid re-initializing on return from FC
  // while still allowing initialization when switching to a different alert.
  const initializedSensorAlertIdRef = useRef<number | null>(null);

  // Reset the init guard when the drawer closes so re-opening the same alert
  // re-runs sensor initialization (loadSensors, setSelectedSensorIds, etc.).
  useEffect(() => {
    if (!isOpen) {
      initializedSensorAlertIdRef.current = null;
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    if (!insightAlertStore.operationRuleAlert) return;
    const alertId = insightAlertStore.operationRuleAlert.id;
    if (initializedSensorAlertIdRef.current === alertId) return;

    const sensorConfig = insightAlertStore.operationRuleAlert.sensorAlertConfig;
    if (!sensorConfig || !sensorConfig.sensors?.length) return;

    // Load precipitation sensors so the SensorAlertSection can display them.
    // Defer setting the init flag until macroRegionOrigin is available so the
    // effect can re-run if the alert loads before macroRegionOrigin resolves.
    if (macroRegionOrigin) {
      insightAlertStore.loadSensors();
    } else {
      return; // retry on next render when macroRegionOrigin becomes available
    }

    initializedSensorAlertIdRef.current = alertId;

    // Only populate store if no pending selection exists (avoids overwriting user changes)
    if (!insightAlertStore.selectedSensorIds?.length) {
      const sensorIds = sensorConfig.sensors.map((s) => s.stationId);
      insightAlertStore.setSelectedSensorIds(sensorIds);
    }

    const conditions = sensorConfig.conditions ?? sensorConfig.sensors[0]?.conditions;
    if (conditions?.length && !insightAlertStore.sensorConditions?.length) {
      insightAlertStore.setSensorConditions(conditions);
    }
  }, [isOpen, insightAlertStore.operationRuleAlert, macroRegionOrigin, insightAlertStore]);

  // Build initialSensorAlertConfig for SensorAlertSection edit mode (FR-027, FR-029).
  // Spec 043: also hydrate the optional `lightning` slice. Lightning-only rows
  // must still produce a non-undefined initial config so the drawer can render
  // the Raio tab.
  //
  // Pending-selection precedence (return from /forecast-charts gateway): the
  // canonical `selectedSensorIds` and `lightningSelection` in the store may
  // already reflect changes the user just made on the gateway. We treat those
  // as the source of truth for `sensors` / `lightning`, falling back to the
  // server-side `sensorAlertConfig` only when no pending selection exists.
  const initialSensorAlertConfig = useMemo((): SensorAlertConfig | undefined => {
    const sensorConfig = insightAlertStore.operationRuleAlert?.sensorAlertConfig;
    const pendingSensorIds = insightAlertStore.selectedSensorIds;
    const pendingLightning = insightAlertStore.lightningSelection;

    const hasPendingRain = (pendingSensorIds?.length ?? 0) > 0;
    const hasPendingLightning = !!pendingLightning;
    const hasServerRain = !!sensorConfig?.sensors?.length;
    const hasServerLightning = !!sensorConfig?.lightning;

    const hasRain = hasPendingRain || hasServerRain;
    const hasLightning = hasPendingLightning || hasServerLightning;
    if (!hasRain && !hasLightning) return undefined;

    // Resolve station names from loaded sensor data so the drawer's sensor
    // selector can render labels for sensors picked up via the gateway.
    const sensorByStationId = new Map(
      insightAlertStore.sensorMeasuredData.map((s) => [s.station_id, s]),
    );

    let sensors: SensorAlertConfig["sensors"] = [];
    if (hasPendingRain) {
      sensors = pendingSensorIds.map((stationId) => {
        const s = sensorByStationId.get(stationId);
        const fromServer = sensorConfig?.sensors?.find(
          (sv) => sv.stationId === stationId,
        );
        return {
          stationId,
          regionId: s?.region_id ?? fromServer?.regionId ?? 0,
          stationName: s?.station_name ?? "",
        };
      });
    } else if (hasServerRain && sensorConfig?.sensors) {
      sensors = sensorConfig.sensors.map((s) => ({
        stationId: s.stationId,
        regionId: s.regionId,
        stationName: "",
      }));
    }

    return {
      sensors,
      conditions:
        sensorConfig?.conditions ??
        sensorConfig?.sensors?.[0]?.conditions ??
        [],
      alertFrequency: sensorConfig?.alertFrequency,
      lightning: hasPendingLightning
        ? pendingLightning
        : hasServerLightning
          ? sensorConfig?.lightning
          : undefined,
    };
  }, [
    insightAlertStore.operationRuleAlert?.sensorAlertConfig,
    insightAlertStore.selectedSensorIds,
    insightAlertStore.lightningSelection,
    insightAlertStore.sensorMeasuredData,
  ]);

  useEffect(() => {
    if (!isOpen || !insightAlertStore.operationRuleAlert) return;

    const { stationId } = insightAlertStore.operationRuleAlert;
    const stations = oceanWeatherIntelligenceStore.stations;

    if (stationId && stations?.length && !insightAlertStore.selectedStationForAlert) {
      const station = stations.find((s: IStation) => s.station_id === stationId);
      if (station) {
        insightAlertStore.setSelectedStationForAlert(station);
      }
    }
  }, [isOpen, oceanWeatherIntelligenceStore.stations, insightAlertStore]);

  useEffect(() => {
    if (insightAlertStore.selectedAlertType === AlertModule.INSIGHT) return;

    if (insightAlertStore.operationRuleAlertAction === DBAction.CREATE) {
      const createValues: EditAlertFormValues = {
        name: "",
        channels: ["whatsapp"],
        selectedUsers: [],
        alertType: "",
        customTitle: templates.defaultTemplate.title,
        customDescription: templates.defaultTemplate.description,
        minAdvanceTime: DEFAULT_MIN_ADVANCE_TIME,
        maxAdvanceTime: LEGACY_MAX_ADVANCE_TIME_FALLBACK,
        minimumEventDuration: 60,
        receiptType: ["sendUpdates", "sendCancellations"],
        startTime: "00:00",
        endTime: "23:59",
        macroRegionId: undefined,
        stationRegion: undefined,
      };

      formData.updateFormValues(createValues);
      templates.setAlertTitle(templates.defaultTemplate.title);
      templates.setAlertDescription(templates.defaultTemplate.description);
      templates.setSelectedTemplateType(
        insightAlertStore.alertTemplates[0]?.alertType ?? null
      );

      insightAlertStore.setOperationRuleAlertUserSelected([]);
      users.setUsersTableSelected([]);
    } else if (insightAlertStore.operationRuleAlertAction === DBAction.UPDATE) {
      if (!insightAlertStore.operationRuleAlert || !isOpen) return;

      const { recipients, notifications, stationId, regionId, geofence, locationId, macroRegionOrigin } =
        insightAlertStore.operationRuleAlert;

      if (!hasDetectedLocationTypeRef.current) {
        hasDetectedLocationTypeRef.current = true;

        // PRIORITY 1: If user has drawn a custom geofence (selectedGeofence exists AND it's not from a saved location)
        // This takes precedence over API data because user drew a new area
        // Note: isGeofenceSavedToLocation=true means user selected from dropdown, not drew custom area
        const hasUserDrawnGeofence = insightAlertStore.selectedGeofence?.length > 0 &&
          !insightAlertStore.isGeofenceSavedToLocation;
        if (hasUserDrawnGeofence) {
          insightAlertStore.setAlertLocationType("area");
          // Clear any station selection since user chose to draw an area instead
          insightAlertStore.setSelectedStationForAlert(null);
        } else if (insightAlertStore.selectedStationForAlert) {
          // PRIORITY 2: If user has selected a station (point)
          insightAlertStore.setAlertLocationType("point");
        } else {
          // PRIORITY 3: Detect from API data
          const isNestedArray = geofence?.length === 1 && Array.isArray(geofence[0]);
          const hasValidNestedStructure = isNestedArray && (geofence[0] as LatLngLiteral[]).length === 1;
          const hasValidFlatStructure = geofence?.length === 1 && !Array.isArray(geofence[0]) &&
            typeof (geofence[0] as LatLngLiteral)?.lat === 'number';

          let customPointCoord: LatLngLiteral | null = null;

          if (hasValidNestedStructure) {
            const firstRing = geofence[0] as LatLngLiteral[];
            customPointCoord = firstRing[0];
          } else if (hasValidFlatStructure) {
            customPointCoord = geofence[0] as LatLngLiteral;
          }

          const isValidSinglePoint = customPointCoord &&
            typeof customPointCoord.lat === 'number' &&
            typeof customPointCoord.lng === 'number' &&
            !isNaN(customPointCoord.lat) &&
            !isNaN(customPointCoord.lng);
          const isCustomPoint = !stationId && isValidSinglePoint;

          if (isCustomPoint && customPointCoord) {
            insightAlertStore.setAlertLocationType("point");

            const customPoint: IStation = {
              station_id: 0,
              station_name: `${customPointCoord.lat.toFixed(4)}, ${customPointCoord.lng.toFixed(4)}`,
              station_lat: customPointCoord.lat,
              station_lon: customPointCoord.lng,
              station_depth: 0,
              station_depth_unit: "m",
              station_save_by_user: false,
              macro_region_origin: macroRegionOrigin || undefined,
            };

            insightAlertStore.setSelectedStationForAlert(customPoint);

            if (macroRegionOrigin) {
              insightAlertStore.setMacroRegionOrigin(macroRegionOrigin as MacroRegion);
            }
          } else if (stationId && regionId) {
            insightAlertStore.setAlertLocationType("point");

            const macroRegion = regionStore.macroRegions?.find(
              (region) => region.origin === macroRegionOrigin
            );

            if (macroRegion) {
              oceanWeatherIntelligenceStore.getStationForLocationAPI(macroRegion.id);

              if (macroRegion.origin) {
                insightAlertStore.setMacroRegionOrigin(
                  macroRegion.origin as MacroRegion
                );
              }
            } else {
              oceanWeatherIntelligenceStore.getStationForLocationAPI(regionId);

              if (macroRegionOrigin) {
                insightAlertStore.setMacroRegionOrigin(
                  macroRegionOrigin as MacroRegion
                );
              }
              console.warn(`Macro region with origin ${macroRegionOrigin} not found. The alert may have been created with a region that no longer exists.`);
            }
          } else if (geofence?.length || locationId) {
            insightAlertStore.setAlertLocationType("area");
          }
        }

        // Mark initial detection as complete
        setHasCompletedInitialDetection(true);
      }

      const recipientSelected = recipients.filter((r) => r.id > 0);
      insightAlertStore.setOperationRuleAlertUserSelected(recipientSelected);
      insightAlertStore.setOperationRuleAlertUserSelectedBeforeEdit(
        recipientSelected
      );

      const selectRowsId = recipientSelected?.map((r) => r.userId);
      users.setUsersTableSelected(selectRowsId);

      const selectedTemplate = insightAlertStore.alertTemplates.find(
        (t) => t.id === insightAlertStore.operationRuleAlert?.alertTemplateId
      );

      templates.setSelectedTemplateType(selectedTemplate?.alertType || "");

      insightAlertStore.setMacroRegionOrigin(
        insightAlertStore.operationRuleAlert?.macroRegionOrigin || null
      );

      if (insightAlertStore.operationRuleAlert?.locationId && !insightAlertStore.selectedGeofence?.length) {
        insightAlertStore.setIsGeofenceSavedToLocation(true);
        insightAlertStore.setAreaSelectorVisible(true);
      }

      const alertMacroRegionOrigin = insightAlertStore.operationRuleAlert?.macroRegionOrigin;
      const macroRegionForForm = regionStore.macroRegions?.find(
        (region) => region.origin === alertMacroRegionOrigin
      );
      const macroRegionIdForForm = macroRegionForForm?.id;

      // Use selectedGeofence if it exists (user may have drawn a new area), otherwise use original
      const hasUserDrawnGeofence = insightAlertStore.selectedGeofence?.length > 0;
      const currentGeofence = normalizeGeofence(
        insightAlertStore.selectedGeofence,
        insightAlertStore.operationRuleAlert?.geofence
      );

      // If user drew a custom geofence (not selected from dropdown), clear locationId and area
      // isGeofenceSavedToLocation=true means user SELECTED an area from dropdown, so keep locationId
      // isGeofenceSavedToLocation=false means user DREW a custom area, so clear locationId
      const isGeofenceFromSelection = insightAlertStore.isGeofenceSavedToLocation;
      const currentLocationId = (hasUserDrawnGeofence && !isGeofenceFromSelection)
        ? undefined
        : insightAlertStore.operationRuleAlert?.locationId;
      const currentArea = (hasUserDrawnGeofence && !isGeofenceFromSelection)
        ? ""
        : (insightAlertStore.operationRuleAlert?.area || "");

      let values: EditAlertFormValues = {
        name: insightAlertStore.operationRuleAlert?.name || "",
        alertType: insightAlertStore.operationRuleAlert?.alertType || "",
        selectedUsers: selectRowsId,
        customTitle: templates.defaultTemplate.title,
        customDescription: templates.defaultTemplate.description,
        minAdvanceTime: DEFAULT_MIN_ADVANCE_TIME,
        maxAdvanceTime: LEGACY_MAX_ADVANCE_TIME_FALLBACK,
        minimumEventDuration: 60,
        receiptType: ["sendUpdates", "sendCancellations"],
        conditionGroups: insightAlertStore.operationRuleAlert?.conditionGroups,
        geofence: currentGeofence,
        area: currentArea,
        locationId: currentLocationId,
        channels: (insightAlertStore.operationRuleAlert?.channels || ["whatsapp"]) as AlertChannel[],
        macroRegionId: macroRegionIdForForm,
        stationRegion: insightAlertStore.operationRuleAlert?.stationId && macroRegionIdForForm
          ? `${insightAlertStore.operationRuleAlert.stationId}-${macroRegionIdForForm}`
          : undefined,
        // Insight alert fields - initialize with current values for editing
        insightId: insightAlertStore.operationRuleAlertForTableForEdit?.operationRuleLocationItemId,
        dashboardId: insightAlertStore.selectedDashboardIdForEdit ?? undefined,
        // Sensor alert config — populate from loaded alert for Formik-driven editing
        sensorAlertConfig: initialSensorAlertConfig,
      };

      if (notifications?.length) {
        const {
          title,
          description,
          sendUpdates,
          sendCancellations,
          minutesType,
          minAdvanceTime,
          maxAdvanceTime,
          minimumEventDuration,
          startTime,
          endTime,
        } = notifications[0];

        let finalMinAdvanceTime = minAdvanceTime ?? DEFAULT_MIN_ADVANCE_TIME;
        let finalMaxAdvanceTime = maxAdvanceTime ?? (minutesType || LEGACY_MAX_ADVANCE_TIME_FALLBACK);

        values = {
          ...values,
          customTitle: title,
          customDescription: description,
          minAdvanceTime: finalMinAdvanceTime,
          maxAdvanceTime: finalMaxAdvanceTime,
          minimumEventDuration: minimumEventDuration ?? 60,
          receiptType: formData.buildReceiptArray(
            sendUpdates,
            sendCancellations
          ),
          startTime: startTime || "00:00",
          endTime: endTime || "23:59",
        };
        templates.setAlertTitle(title);
        templates.setAlertDescription(description);
      } else {
        templates.setAlertTitle(templates.defaultTemplate.title);
        templates.setAlertDescription(templates.defaultTemplate.description);
      }

      formData.updateFormValues(values);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    insightAlertStore.operationRuleAlertUser,
    insightAlertStore.alertTemplates,
    insightAlertStore.operationRuleAlert,
    insightAlertStore.selectedAlertType,
    insightAlertStore.operationRuleAlertAction,
    insightAlertStore.selectedGeofence,
    isOpen,
  ]);

  const handleSubmit = (value: EditAlertFormValues) => {
    submission.handleOnSaveFormAlertModal(
      value,
      value.alertType || "",
      users.clearUserTableSelected,
      onClose
    );
  };

  const clearGeofence = useCallback(() => {
    if (insightAlertStore.operationRuleAlert) {
      insightAlertStore.setOperationRuleAlert({
        ...insightAlertStore.operationRuleAlert,
        geofence: undefined,
      });
    }
    insightAlertStore.setIsGeofenceInvalid(false);
    insightAlertStore.setIsGeofenceLoadedByGeoJson(false);
    insightAlertStore.setSelectedGeofence([]);
    insightAlertStore.setIsGeofenceSavedToLocation(false);
  }, [insightAlertStore]);

  const handleGeoJsonLoaded = useCallback((geojson: any) => {
    if (!geojson) return;

    insightAlertStore.setIsGeofenceLoadedByGeoJson(true);

    const coordinates = extractCoordinatesFromGeoJSON(geojson);
    const macroRegion = getMacroRegionOriginFromPolygon(
      coordinates,
      regionStore.macroRegions,
      authStore.userMarcroRegions as MacroRegion[]
    );
    if (!insightAlertStore.operationRuleAlert) return;

    insightAlertStore.setOperationRuleAlert({
      ...insightAlertStore.operationRuleAlert,
      geofence: coordinates,
    });
    insightAlertStore.setSelectedGeofence(coordinates);
    if (macroRegion) {
      insightAlertStore.setMacroRegionOrigin(macroRegion);
      insightAlertStore.setIsGeofenceInvalid(false);
    } else {
      insightAlertStore.setIsGeofenceInvalid(true);
      insightAlertStore.setMacroRegionOrigin(null);
    }
  }, [authStore.userMarcroRegions, insightAlertStore, regionStore.macroRegions]);

  const handleDrawingMode = useCallback(() => {
    forecastChartPageStore.setIsAreaDrawingMode(true);

    const hasExistingSelection = insightAlertStore.selectedGeofence.length > 0;
    const alertGeofence = insightAlertStore.operationRuleAlert?.geofence;

    if (!hasExistingSelection && alertGeofence?.length) {
      insightAlertStore.setSelectedGeofence(alertGeofence as LatLngLiteral[][]);
    }

    const alertId = insightAlertStore.operationRuleAlert?.id;
    history.push(`/forecast-charts?alert_id=${alertId}`);
  }, [forecastChartPageStore, history, insightAlertStore]);

  const handlePointSelectionMode = useCallback(() => {
    insightAlertStore.setOriginalStationForAlert(
      insightAlertStore.selectedStationForAlert
    );
    forecastChartPageStore.setIsPointSelectionMode(true);
    const alertId = insightAlertStore.operationRuleAlert?.id;
    history.push(`/forecast-charts?alert_id=${alertId}`);
  }, [forecastChartPageStore, history, insightAlertStore]);

  const clearSelectedStation = useCallback(() => {
    insightAlertStore.setSelectedStationForAlert(null);
  }, [insightAlertStore]);

  const handleChangeGeofence = useCallback((newGeofence: LatLngLiteral[] | LatLngLiteral[][]) => {
    insightAlertStore.setSelectedGeofence(newGeofence);
  }, [insightAlertStore]);

  const handleAreaSelection = useCallback(
    (selectedAreaId: number, areas: any[], setFieldValue: (field: string, value: any) => void) => {
      setFieldValue("locationId", selectedAreaId);

      const selectedArea = areas.find((area) => area.id === selectedAreaId);
      if (selectedArea && selectedArea.coordinates) {
        insightAlertStore.setSelectedGeofence(selectedArea.coordinates);
        setFieldValue("geofence", selectedArea.coordinates);
        setFieldValue("area", selectedArea.name);

        // Mark that this geofence is from a saved location selection (not custom drawn)
        insightAlertStore.setIsGeofenceSavedToLocation(true);

        // Also update operationRuleAlert to keep it in sync
        if (insightAlertStore.operationRuleAlert) {
          insightAlertStore.setOperationRuleAlert({
            ...insightAlertStore.operationRuleAlert,
            locationId: selectedAreaId,
            geofence: selectedArea.coordinates,
            area: selectedArea.name,
          });
        }

        const macroRegion = getMacroRegionOriginFromPolygon(
          [selectedArea.coordinates],
          regionStore.macroRegions,
          authStore.userMarcroRegions as MacroRegion[]
        );

        if (macroRegion) {
          insightAlertStore.setMacroRegionOrigin(macroRegion);
          insightAlertStore.setIsGeofenceInvalid(false);
        } else {
          insightAlertStore.setIsGeofenceInvalid(true);
          insightAlertStore.setMacroRegionOrigin(null);
        }
      }
    },
    [insightAlertStore, regionStore.macroRegions, authStore.userMarcroRegions]
  );

  const setIsGeofenceLoadedByGeoJson = useCallback((value: boolean) => {
    insightAlertStore.setIsGeofenceLoadedByGeoJson(value);
  }, [insightAlertStore]);

  const handleEnableAreaSelector = useCallback((value: boolean) => {
    insightAlertStore.setAreaSelectorVisible(value);
  }, [insightAlertStore]);

  const handleLocationTypeSelect = useCallback((
    type: LocationType,
    setFieldValue: (field: string, value: any) => void
  ) => {
    setFieldValue("macroRegionId", undefined);
    setFieldValue("stationRegion", undefined);
    setFieldValue("geofence", []);
    setFieldValue("locationId", undefined);
    clearGeofence();
    clearSelectedStation();

    if (insightAlertStore.operationRuleAlert) {
      insightAlertStore.setOperationRuleAlert({
        ...insightAlertStore.operationRuleAlert,
        stationId: undefined,
        regionId: undefined,
        geofence: undefined,
        locationId: undefined,
      });
    }

    insightAlertStore.setAlertLocationType(type);

    if (type === "point") {
      handlePointSelectionMode();
    }
  }, [insightAlertStore, clearGeofence, clearSelectedStation, handlePointSelectionMode]);

  // Actually execute the back action (clear location data)
  const executeBackAction = useCallback((
    setFieldValue: (field: string, value: any) => void
  ) => {
    setFieldValue("macroRegionId", undefined);
    setFieldValue("stationRegion", undefined);
    setFieldValue("geofence", undefined);
    setFieldValue("locationId", undefined);

    if (insightAlertStore.operationRuleAlert) {
      insightAlertStore.setOperationRuleAlert({
        ...insightAlertStore.operationRuleAlert,
        stationId: undefined,
        regionId: undefined,
        geofence: undefined,
        locationId: undefined,
      });
    }

    clearSelectedStation();
    clearGeofence();

    insightAlertStore.setAlertLocationType(null);
    insightAlertStore.setAreaSelectorVisible(false);
  }, [insightAlertStore, clearSelectedStation, clearGeofence]);

  // Execute back from geofence to MapDrawOptions (keeps area type, just clears geofence)
  const executeBackFromGeofence = useCallback((
    setFieldValue: (field: string, value: any) => void
  ) => {
    setFieldValue("geofence", []);
    setFieldValue("locationId", undefined);
    setFieldValue("area", undefined);

    if (insightAlertStore.operationRuleAlert) {
      insightAlertStore.setOperationRuleAlert({
        ...insightAlertStore.operationRuleAlert,
        geofence: undefined,
        locationId: undefined,
      });
    }

    clearGeofence();
    insightAlertStore.setAreaSelectorVisible(false);
    // Keep alertLocationType as "area" to show MapDrawOptions
  }, [insightAlertStore, clearGeofence]);

  // Handler for back from GeofenceMapContainer - goes back to MapDrawOptions (not to Point/Area selector)
  const handleBackFromGeofence = useCallback((
    setFieldValue: (field: string, value: any) => void
  ) => {
    // Check if there's geofence data that would be lost
    const hasGeofence = insightAlertStore.selectedGeofence && insightAlertStore.selectedGeofence.length > 0;
    const hasOperationGeofence = insightAlertStore.operationRuleAlert?.geofence &&
      insightAlertStore.operationRuleAlert.geofence.length > 0;
    const hasGeofenceData = hasGeofence || hasOperationGeofence;

    if (hasGeofenceData) {
      pendingBackActionRef.current = () => executeBackFromGeofence(setFieldValue);
      setIsLocationWarningModalOpen(true);
    } else {
      executeBackFromGeofence(setFieldValue);
    }
  }, [insightAlertStore, executeBackFromGeofence]);

  const handleBack = useCallback((
    setFieldValue: (field: string, value: any) => void
  ) => {
    // Check if there's location data that would be lost (inline check)
    const hasStation = !!insightAlertStore.selectedStationForAlert;
    const hasGeofence = insightAlertStore.selectedGeofence && insightAlertStore.selectedGeofence.length > 0;
    const hasOperationGeofence = insightAlertStore.operationRuleAlert?.geofence && insightAlertStore.operationRuleAlert.geofence.length > 0;
    const hasOperationStation = !!insightAlertStore.operationRuleAlert?.stationId;
    const hasLocationData = hasStation || hasGeofence || hasOperationGeofence || hasOperationStation;

    if (hasLocationData) {
      // Store the action to execute after confirmation
      pendingBackActionRef.current = () => executeBackAction(setFieldValue);
      setIsLocationWarningModalOpen(true);
    } else {
      // No data to lose, just go back
      insightAlertStore.setAlertLocationType(null);
      insightAlertStore.setAreaSelectorVisible(false);
    }
  }, [insightAlertStore, executeBackAction]);

  const handleLocationWarningConfirm = useCallback(() => {
    if (pendingBackActionRef.current) {
      pendingBackActionRef.current();
      pendingBackActionRef.current = null;
    }
    setIsLocationWarningModalOpen(false);
  }, []);

  const handleLocationWarningCancel = useCallback(() => {
    pendingBackActionRef.current = null;
    setIsLocationWarningModalOpen(false);
  }, []);

  const handleChangeRegion = useCallback((regionId: number) => {
    oceanWeatherIntelligenceStore.getStationForLocationAPI(regionId);
  }, [oceanWeatherIntelligenceStore]);

  const handleChangeStation = useCallback((
    stationId: number,
    regionId: number,
    setFieldValue: (field: string, value: any) => void
  ) => {
    const station = oceanWeatherIntelligenceStore.stations?.find(
      (s: any) => s.station_id === stationId
    );

    if (station) {
      // Only update stationRegion, don't change macroRegionId as it's already set by the region select
      setFieldValue("stationRegion", `${stationId}-${regionId}`);

      const macroRegionOrigin = station.macro_region_origin;
      if (macroRegionOrigin) {
        insightAlertStore.setMacroRegionOrigin(macroRegionOrigin as MacroRegion);
      }
    }
  }, [oceanWeatherIntelligenceStore, insightAlertStore]);

  return {
    t,
    ...formData,
    ...templates,
    ...users,
    handleOnSaveFormAlertModal: handleSubmit,
    selectedGeofence: insightAlertStore.selectedGeofence,
    environmentalVariables: insightAlertStore.environmentalVariables,
    // Use selectedGeofence if available (user may have drawn a new area), otherwise use operationRuleAlert.geofence
    geofence: insightAlertStore.selectedGeofence?.length > 0
      ? insightAlertStore.selectedGeofence
      : (insightAlertStore.operationRuleAlert?.geofence || null),
    areas: locationStore.areas,
    clearGeofence,
    handleGeoJsonLoaded,
    handleDrawingMode,
    handlePointSelectionMode,
    clearSelectedStation,
    handleChangeGeofence,
    handleAreaSelection,
    setIsGeofenceLoadedByGeoJson,
    handleEnableAreaSelector,
    handleLocationTypeSelect,
    handleBack,
    handleBackFromGeofence,
    handleChangeRegion,
    handleChangeStation,
    isLoading: insightAlertStore.alertDetailState === StoreState.PEDDING,
    isSaving: insightAlertStore.submissionState === StoreState.PEDDING,
    // Only show detecting location type loading during initial detection, not when user navigates back
    isDetectingLocationType:
      insightAlertStore.operationRuleAlertAction === DBAction.UPDATE &&
      !insightAlertStore.alertLocationType &&
      !hasCompletedInitialDetection,
    areaSelectorVisible: insightAlertStore.areaSelectorVisible,
    alertLocationType: insightAlertStore.alertLocationType,
    selectedStationForAlert: insightAlertStore.selectedStationForAlert,
    // Location warning modal
    isLocationWarningModalOpen,
    handleLocationWarningConfirm,
    handleLocationWarningCancel,
    // Insight Alert data
    isInsightAlert,
    insightAlertData,
    // Sensor alert data for SensorAlertSection (Phase 10)
    initialSensorAlertConfig,
    sensorConditions: insightAlertStore.sensorConditions,
    sensorAlertFrequency: insightAlertStore.operationRuleAlert?.sensorAlertConfig?.alertFrequency ?? 60,
    // Spec 043: lightning-only rows (no rain sensors) must also render SensorAlertSection.
    hasSensorAlertConfig: !!(
      insightAlertStore.operationRuleAlert?.sensorAlertConfig?.sensors?.length ||
      insightAlertStore.operationRuleAlert?.sensorAlertConfig?.lightning
    ),
  };
}
